import React, { memo, useCallback } from 'react'

import {
  Toggle, Icon, InfoOutlined, Tooltip,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  string, func, bool, node,
} from 'prop-types'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'

function ToggleSelector({
  id,
  keyName,
  sliceName,
  setFunction,
  label,
  disabled = false,
  tooltipMessage = undefined,
  badge = null,
}) {
  const isActive = useThreeDStudioStore(
    (state) => state[sliceName]?.[keyName] ?? state[keyName]
  )

  // setFunction must be in the deps: callers can pass a handler whose behavior
  // depends on state that hydrates after mount (e.g. PrivateModeSelector's
  // upsell handler reads userLoggedIn). Omitting it memoizes the first handler
  // and later clicks fire a stale closure.
  const handleToggle = useCallback(() => {
    setFunction(!isActive)
  }, [isActive, setFunction])

  // With a badge the label sits after the badge, so the Toggle renders switch-only
  // and the label is a sibling <label> kept associated via htmlFor.
  const toggleComponent = (
    <Toggle
      id={id}
      label={badge ? undefined : label}
      className="model-settings__toggle"
      isChecked={isActive}
      onChange={handleToggle}
      disabled={disabled}
    />
  )

  const infoTooltip = tooltipMessage && (
    <Tooltip
      id={`${id}-tooltip`}
      content={tooltipMessage}
      tooltipClassName="model-settings__tooltip"
      event="click"
      place="top"
    >
      <button type="button" className="model-settings__tooltip-trigger" aria-label={label}>
        <Icon svgComponent={InfoOutlined} color="darker" size="small" notSpaced />
      </button>
    </Tooltip>
  )

  if (!badge && !tooltipMessage) {
    return toggleComponent
  }

  const containerClassName = badge
    ? 'model-settings__toggle-container model-settings__toggle-container--with-badge'
    : 'model-settings__toggle-container'

  return (
    <div className={containerClassName}>
      {toggleComponent}
      {badge}
      {badge && (
        <label htmlFor={id} className="model-settings__toggle-label cgt-body-base-semibold">{label}</label>
      )}
      {infoTooltip}
    </div>
  )
}

ToggleSelector.propTypes = {
  id: string.isRequired,
  keyName: string.isRequired,
  sliceName: string.isRequired,
  setFunction: func.isRequired,
  label: string.isRequired,
  disabled: bool,
  tooltipMessage: string,
  badge: node,
}

export default memo(ToggleSelector)
